import React from "react";
import { Modal, Tag, Typography, Divider } from "antd";

import css from "./QuizResponseDetailsModal.module.css";

const { Text, Title } = Typography;

// Legacy fixed fields a pre-dynamic-quiz (old-shape) row may have populated
// instead of the generic `answers` map — shown as a fallback section so
// those older rows don't lose data once the fixed columns/logic are gone.
const LEGACY_FIELDS = [
  { key: "primaryGoal", label: "Primary Goal" },
  { key: "dietaryFocus", label: "Dietary Focus" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "activityLevel", label: "Activity Level" },
  { key: "caloriePreference", label: "Calorie Preference" },
  { key: "excludedMeats", label: "Excluded Meats", isTagList: true },
  { key: "medicalConditions", label: "Medical Conditions", isTagList: true },
];

// This modal NEVER matches a row's answers against today's live Builder
// config — that's what used to cause impossible/confusing results (a row
// answering 9 questions compared against a quiz that now only has 8, or a
// blank "Unable to load configuration" for a row with real data sitting
// right there). Each row is rendered from exactly one of three
// self-contained states, decided purely by what that row itself has:
//   1. hasSnapshot   -> readable question/answer text frozen at submission
//      time (see wellness_quiz.service.ts's buildAnswersSnapshot) — always
//      correct, forever, regardless of later Builder edits.
//   2. hasRawAnswers -> pre-fix row: only raw {questionId: answerId} ids
//      were ever saved, no readable text was captured. Shown as-is (literal
//      ids) rather than guessed against a config that may have moved on —
//      honest about what we actually know instead of showing something
//      that looks resolved but might be wrong.
//   3. legacy        -> pre-dynamic-quiz row using the old fixed columns
//      (primaryGoal, dietaryFocus, ...) — literal text was always stored
//      directly, nothing to resolve.
function QuizResponseDetailsModal({ isOpen, setIsOpen, record, packCategoriesById, contactExtraFieldsById }) {
  if (!record) return null;

  const hasSnapshot = Array.isArray(record.answersSnapshot) && record.answersSnapshot.length > 0;
  const hasRawAnswers = !hasSnapshot && record.answers && Object.keys(record.answers).length > 0;
  // Prefer the point-in-time snapshot (label frozen at submission time, so
  // a later Builder edit/delete of that field can't blank/corrupt it) —
  // only fall back to resolving raw ids against the CURRENT config for
  // pre-snapshot rows that don't have one yet.
  const hasExtraContactFieldsSnapshot = Array.isArray(record.extraContactFieldsSnapshot) && record.extraContactFieldsSnapshot.length > 0;
  const extraContactFieldEntries = hasExtraContactFieldsSnapshot
    ? record.extraContactFieldsSnapshot.map((entry) => [entry.fieldId, entry.value, entry.label])
    : Object.entries(record.extraContactFields || {}).filter(([, v]) => !!v);
  const legacyFieldsWithData = LEGACY_FIELDS.filter((f) => {
    const val = record[f.key];
    return f.isTagList ? Array.isArray(val) && val.length > 0 : !!val;
  });
  const isLegacy = !hasSnapshot && !hasRawAnswers && legacyFieldsWithData.length > 0;
  const recommendedCategoryName = record.recommendedPackCategoryId
    ? packCategoriesById?.[record.recommendedPackCategoryId] || record.recommendedPackCategoryId
    : null;

  return (
    <Modal
      title="Quiz Response Details"
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={null}
      width={720}
      destroyOnClose
    >
      <div className={css.headerInfo}>
        <div className={css.headerRow}>
          <Text type="secondary">Full Name</Text>
          <Text strong>{record.fullName}</Text>
        </div>
        <div className={css.headerRow}>
          <Text type="secondary">Email</Text>
          <Text>{record.email}</Text>
        </div>
        <div className={css.headerRow}>
          <Text type="secondary">Phone</Text>
          <Text>{record.phone}</Text>
        </div>
        {recommendedCategoryName && (
          <div className={css.headerRow}>
            <Text type="secondary">Recommended Plan</Text>
            <Tag color="green">{recommendedCategoryName}</Tag>
          </div>
        )}
        {extraContactFieldEntries.map(([fieldId, value, snapshotLabel]) => (
          <div className={css.headerRow} key={fieldId}>
            <Text type="secondary">{snapshotLabel || contactExtraFieldsById?.[fieldId] || fieldId}</Text>
            <Text>{value}</Text>
          </div>
        ))}
      </div>

      <Divider />

      {hasSnapshot && (
        <>
          <Title level={5}>Questions &amp; Answers</Title>
          <div className={css.qaList}>
            {record.answersSnapshot.map((entry) => (
              <div className={css.qaRow} key={entry.questionId}>
                <Text className={css.qaQuestion}>{entry.questionText}</Text>
                {entry.answered ? (
                  <Text className={css.qaAnswer}>{entry.answerLabels.join(", ")}</Text>
                ) : (
                  <Text type="secondary" className={css.notAnswered}>
                    Not answered
                  </Text>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {hasRawAnswers && (
        <>
          <Title level={5}>Questions &amp; Answers</Title>
          <Text type="secondary" className={css.notAnswered}>
            Recorded before readable answers were saved — showing raw ids.
          </Text>
          <div className={css.qaList}>
            {Object.entries(record.answers).map(([questionId, value]) => (
              <div className={css.qaRow} key={questionId}>
                <Text className={css.qaQuestion}>{questionId}</Text>
                <Text className={css.qaAnswer}>
                  {Array.isArray(value) ? value.join(", ") : value}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}

      {isLegacy && (
        <>
          <Title level={5}>Questions &amp; Answers</Title>
          <div className={css.qaList}>
            {legacyFieldsWithData.map((f) => (
              <div className={css.qaRow} key={f.key}>
                <Text className={css.qaQuestion}>{f.label}</Text>
                {f.isTagList ? (
                  <div className={css.tagCell}>
                    {record[f.key].map((v) => (
                      <Tag key={v}>{v}</Tag>
                    ))}
                  </div>
                ) : (
                  <Text className={css.qaAnswer}>{record[f.key]}</Text>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

export { QuizResponseDetailsModal };
